// Laufzeit der Oberfläche: der eine Abfrage-Cache, die Echtzeit-Brücke, die Verdrahtung von Client, Sitzung und
// Verbindungsanzeige – und die Wurzel-Bausteine des Routers (Anbieter, Sitzungsprüfung, Absturzseite).
import { useCallback, useEffect, useRef, useState } from 'react'
import { Navigate, Outlet, useLocation, useNavigate, useRouteError } from 'react-router'
import { brand } from '@shared/brand'
import { configureApiClient, getApiClient } from '../api/client'
import { ReauthHost } from '../api/auth/Reauth'
import { ActionConfirmHost, cancelPendingConfirmation } from '../api/confirm'
import { ApiProvider, createQueryClient, useWebModules } from '../api/context'
import { ACTION_KEY } from '../api/keys'
import { syncPushOnStart } from '../api/push'
import { CrashState } from '../api/pages/states'
import { createRealtimeBridge } from '../api/realtime'
import type { AccentStyle } from '../lib/accent'
import { nativeApp, registerNativeOpenPath } from '../lib/native'
import { applyTenantAccent, resetTenantAccent } from '../lib/theme'
import type { WebModule } from '../module'
import { setApiReachable, watchBrowserConnection } from '../stores/connection'
import {
  loadSession,
  markOfflineEntered,
  resumeSession,
  sessionExpired,
  sessionIdentity,
  useSessionStore,
} from '../stores/session'
import { shell } from '../stores/shell'
import { Button } from '../ui/Button'
import { EmptyState } from '../ui/EmptyState'
import { Logo } from '../ui/Logo'
import { UiProvider } from '../ui/UiProvider'
import { probeOfflineStart, useOfflineReconnect } from './offlineStart'
import { AppResume } from './resume'
import { RouterLink } from './RouterLink'

export const queryClient = createQueryClient()
const realtime = createRealtimeBridge({ queryClient })
/** In Komponententests gibt es keinen Server, an dem die Brücke anklopfen könnte. */
const REALTIME_ENABLED = import.meta.env.MODE !== 'test'

configureApiClient({ onUnauthenticated: () => sessionExpired(), onReachability: setApiReachable })

const ACCENT_STYLES: readonly string[] = ['mono', 'deep', 'bright']

/** Wurzel aller Routen der Anwendung: Anbieter des UI-Kits und der API-Schicht, dazu was an der Sitzung hängt. */
export function AppRoot({ modules }: { modules?: readonly WebModule[] }) {
  const identity = useSessionStore((state) => sessionIdentity(state.session))
  const hasTenant = useSessionStore((state) => Boolean(state.session?.tenant))
  const accentColor = useSessionStore((state) => state.session?.tenant?.accentColor ?? null)
  const accentStyle = useSessionStore((state) => state.session?.tenant?.accentStyle ?? null)
  const activeLocationId = useSessionStore((state) => state.session?.activeLocationId ?? null)
  const userId = useSessionStore((state) => state.session?.user?.id ?? null)
  const tenantId = useSessionStore((state) => state.session?.tenant?.id ?? null)
  const navigate = useNavigate()
  // Ersatz-Sitzung (Start ohne Server): kein Echtzeit-Kanal, die gemerkte Hausfarbe bleibt stehen
  const live = useSessionStore((state) => state.status !== 'offline-till')

  useEffect(() => watchBrowserConnection(), [])

  // Nutzer oder Betrieb gewechselt: Nichts aus der vorigen Sitzung darf stehen bleiben; Echtzeit neu verbinden
  const previousIdentity = useRef('')
  useEffect(() => {
    // Nur beim WECHSEL: Erscheint die erste Sitzung ('' → Nutzer), gibt es nichts zu leeren – und die Seite darunter
    // hat ihre Abfragen in diesem Moment schon gestartet (Effekte der Kinder laufen vor diesem); ein clear() nähme
    // sie ihr weg, und eine nicht nachgeladene Seite (/documents/:id) bliebe beim Skelett stehen.
    if (previousIdentity.current !== '' && previousIdentity.current !== identity) {
      queryClient.clear()
      shell.closeAll()
      cancelPendingConfirmation()
    }
    const loggedOut = previousIdentity.current !== '' && identity === ''
    previousIdentity.current = identity
    if (identity && hasTenant && live && REALTIME_ENABLED) void realtime.start()
    else realtime.stop()
    if (loggedOut) resetTenantAccent()
    return () => realtime.stop()
  }, [identity, hasTenant, live])

  // Hausfarbe des Betriebs (design-system §2.3); gemerkt, damit sie beim nächsten Start ohne Aufblitzen steht
  useEffect(() => {
    if (!identity || !hasTenant || !live) return
    if (accentColor && /^#[0-9a-f]{6}$/i.test(accentColor)) {
      const style = accentStyle && ACCENT_STYLES.includes(accentStyle) ? (accentStyle as AccentStyle) : undefined
      applyTenantAccent(accentColor, style, { persist: true })
    } else {
      resetTenantAccent()
    }
  }, [identity, hasTenant, live, accentColor, accentStyle])

  // App: Tippen auf eine Push-Mitteilung öffnet ihren Pfad (die App ruft window.merxoOpenPath, §12.5)
  useEffect(() => (nativeApp() ? registerNativeOpenPath((path) => void navigate(path)) : undefined), [navigate])

  // App: Push-Anmeldung dieses Geräts bei jedem Start bzw. Personen- oder Betriebswechsel auffrischen (§12.4)
  useEffect(() => {
    if (!nativeApp() || !userId || !tenantId || !live) return
    void syncPushOnStart(getApiClient(), { userId, tenantId })
  }, [userId, tenantId, live])

  // Arbeitsstandort gewechselt: Abfragen, die sich am Standort der Sitzung orientieren, laden neu
  const previousLocation = useRef(activeLocationId)
  useEffect(() => {
    if (previousLocation.current !== activeLocationId) {
      previousLocation.current = activeLocationId
      void queryClient.invalidateQueries({ queryKey: [ACTION_KEY] })
    }
  }, [activeLocationId])

  return (
    <UiProvider link={RouterLink}>
      <ApiProvider queryClient={queryClient} modules={modules}>
        <Outlet />
        <ActionConfirmHost />
        {/* frische Bestätigung für sicherheitsrelevante Aktionen jeder Seite (useActionMutation, reauth_required) */}
        <ReauthHost />
      </ApiProvider>
    </UiProvider>
  )
}

/** Ruhiger Startbildschirm, solange die Sitzung geprüft wird (erscheint erst nach kurzer Wartezeit). */
export function Splash() {
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 300)
    return () => clearTimeout(timer)
  }, [])
  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas" aria-busy="true" data-testid="splash">
      {visible && <Logo size={40} title={`${brand.name} wird geladen`} className="animate-pulse-dot" />}
    </div>
  )
}

function Standalone({ children }: { children: React.ReactNode }) {
  return (
    <main className="flex min-h-dvh flex-col items-center bg-canvas px-[max(1rem,env(safe-area-inset-left),env(safe-area-inset-right))] pt-[max(14vh,calc(env(safe-area-inset-top)+2.5rem))] pb-[max(2.5rem,calc(env(safe-area-inset-bottom)+1rem))] text-fg">
      <div className="w-full max-w-xl">
        <div className="flex items-center gap-3 px-(--page-px)">
          <Logo variant="lockup" size={26} />
        </div>
        {children}
      </div>
    </main>
  )
}

/** Text der Fehlerseite für andere Routen, solange nur die Route des Starts ohne Server läuft */
export const OFFLINE_ELSEWHERE_TITLE = 'Diese Seite ist ohne Verbindung zum Server nicht verfügbar'
export const OFFLINE_ELSEWHERE_TEXT =
  'Ohne Verbindung steht nur die Seite bereit, die auf diesem Gerät dafür eingerichtet ist. Alle anderen Seiten öffnen sich wieder, sobald die Verbindung zurück ist.'

/**
 * Prüft die Sitzung vor allem, was eine Anmeldung braucht. Nicht angemeldet → Anmeldeseite mit Rücksprung.
 *
 * Start ohne Server (docs/kasse-offline/SPEZIFIKATION.md §14.7): Scheitert die Sitzung ohne Antwort von Merxo, fragt
 * die Hülle die Proben der Module. Liefert eine einen Kontext, leitet sie von JEDER Route auf deren Pfad um und zeigt nur
 * diese Route (Zustand 'offline-till'); jede andere Route zeigt danach die Fehlerseite. Kommt die Verbindung zurück,
 * ersetzt die echte Sitzung die Ersatz-Sitzung, ohne dass die Route neu entsteht (das <Outlet /> bleibt an seinem Platz).
 */
export function SessionGate() {
  const status = useSessionStore((state) => state.status)
  const session = useSessionStore((state) => state.session)
  const error = useSessionStore((state) => state.error)
  const offline = useSessionStore((state) => state.offline)
  const modules = useWebModules()
  const location = useLocation()
  const navigate = useNavigate()

  const start = useCallback(() => loadSession({ offlineStart: () => probeOfflineStart(modules) }), [modules])
  useEffect(() => {
    if (status === 'unknown') void start()
  }, [status, start])

  const offlineTill = status === 'offline-till' && offline !== null
  const onOfflineRoute = offlineTill && location.pathname === offline.path
  useOfflineReconnect(offlineTill)
  useEffect(() => {
    if (onOfflineRoute) markOfflineEntered()
  }, [onOfflineRoute])
  // Kennzeichen im DOM (Abläufe, Druck-CSS): Die Anwendung läuft mit Ersatz-Sitzung
  useEffect(() => {
    if (!offlineTill) return
    document.documentElement.dataset.session = 'offline-till'
    return () => {
      delete document.documentElement.dataset.session
    }
  }, [offlineTill])

  if (status === 'unknown' || status === 'loading') return <Splash />
  if (status === 'error') {
    return (
      <Standalone>
        <CrashState error={error} onRetry={() => void start()} />
      </Standalone>
    )
  }
  if (offlineTill && !onOfflineRoute) {
    // erster Halt nach dem Start: dorthin, wo die Probe hinführt – gleich, wo die Anwendung gestartet ist
    if (!offline.entered) return <Navigate to={offline.path} replace />
    return (
      <Standalone>
        <div className="flex min-h-0 flex-1 flex-col" data-ui="crash" data-testid="offline-elsewhere">
          <EmptyState
            variant="offline"
            title={OFFLINE_ELSEWHERE_TITLE}
            description={OFFLINE_ELSEWHERE_TEXT}
            action={{ id: 'return', label: offline.context.returnLabel ?? 'Zurück', variant: 'primary', onSelect: () => void navigate(offline.path) }}
            secondaryAction={{ id: 'retry', label: 'Erneut versuchen', onSelect: () => void resumeSession() }}
          />
        </div>
      </Standalone>
    )
  }
  if (!offlineTill) {
    if (status === 'anonymous' || !session) {
      const target = `${location.pathname}${location.search}`
      const next = target === '/' ? '' : `?next=${encodeURIComponent(target)}`
      return <Navigate to={`/login${next}`} replace />
    }
    if (!session.tenant) return <Navigate to="/login" replace />
  }
  // Ersatz-Sitzung und echte Sitzung rendern das <Outlet /> an DERSELBEN Stelle: Der Wechsel lässt die Seite stehen
  return (
    <>
      {/* Merxo-App: nach einem Kaltstart weiter, wo die Person war (web/shell/resume.tsx) */}
      {!offlineTill && session?.tenant ? <AppResume userId={session.user.id} tenantId={session.tenant.id} /> : null}
      <Outlet />
    </>
  )
}

/** Absturz außerhalb einer Seite (Fehler in der Hülle selbst): eigene Seite mit Neuladen. */
export function CrashPage() {
  const error = useRouteError()
  useEffect(() => {
    if (import.meta.env.DEV) console.error(error)
  }, [error])
  return (
    <UiProvider>
      <Standalone>
        <CrashState error={error} />
        <div className="px-(--page-px)">
          <Button variant="ghost" onClick={() => window.location.assign('/')}>
            Zur Startseite
          </Button>
        </div>
      </Standalone>
    </UiProvider>
  )
}
